export default function formSelect(config) {
    return {
        // --- State ---
        open: false,
        search: '',
        highlightIndex: -1,
        multiple: config.multiple ?? false,
        disabled: config.disabled ?? false,
        value: null,
        computedOptions: [],

        // --- Private ---
        _staticItems: config.items ?? [],
        _optionValue: config.optionValue ?? 'id',
        _optionLabel: config.optionLabel ?? 'name',
        _errorKey: config.errorKey ?? '',
        _parentModelExpr: config.parentModelExpr ?? null,
        _initialValue: config.initialValue ?? null,
        _onSelectCallback: config.onSelectCallback ?? null,
        _syncing: false,

        // --- Init ---
        init() {
            if (this.multiple) {
                this.value = Array.isArray(this._initialValue)
                    ? [...this._initialValue]
                    : [];
            } else {
                this.value =
                    this._initialValue !== null &&
                    this._initialValue !== undefined
                        ? this._initialValue
                        : '';
            }

            if (this._staticItems.length > 0) {
                this.computedOptions = this._staticItems.map((item) => {
                    if (typeof item === 'object' && item !== null) {
                        return {
                            value: item.value ?? item[this._optionValue],
                            label: item.label ?? item[this._optionLabel],
                        };
                    }
                    return { value: item, label: item };
                });
            }

            // НЕТ $watch — запись в родителя делаем вручную в select/clear/removeTag
        },

        // --- Запись в родителя ---
        _writeToParent(val) {
            if (!this._parentModelExpr) return;

            this._syncing = true;

            const keys = this._parentModelExpr.split('.');
            const last = keys.pop();
            const target = keys.reduce((obj, key) => obj?.[key], this.$data);

            if (target && last) {
                target[last] = val;
            }

            // Даём x-effect отработать с новым значением, потом снимаем флаг
            queueMicrotask(() => {
                this._syncing = false;
            });
        },

        _valuesEqual(a, b) {
            if (this.multiple) {
                const arrA = Array.isArray(a) ? a.map(String).sort() : [];
                const arrB = Array.isArray(b) ? b.map(String).sort() : [];
                return JSON.stringify(arrA) === JSON.stringify(arrB);
            }
            const strA = a !== null && a !== undefined ? String(a) : '';
            const strB = b !== null && b !== undefined ? String(b) : '';
            return strA === strB;
        },

        // --- Sync из родителя (x-effect) ---
        syncFromParent(parentValue) {
            // Если мы сами только что записали — игнорируем
            if (this._syncing) return;

            if (this.multiple) {
                const newVal = Array.isArray(parentValue) ? parentValue : [];
                if (!this._valuesEqual(this.value, newVal)) {
                    this.value = [...newVal];
                }
            } else {
                const newVal =
                    parentValue !== null && parentValue !== undefined
                        ? parentValue
                        : '';
                if (!this._valuesEqual(this.value, newVal)) {
                    this.value = newVal;
                }
            }
        },

        syncOptions(rawOptions) {
            const opts = (rawOptions || []).map((item) => {
                if (typeof item === 'object' && item !== null) {
                    return {
                        value: item[this._optionValue],
                        label: item[this._optionLabel],
                    };
                }
                return { value: item, label: item };
            });

            if (JSON.stringify(this.computedOptions) !== JSON.stringify(opts)) {
                this.computedOptions = opts;
            }
        },

        // --- Getters ---
        get filteredOptions() {
            if (!this.search) return this.computedOptions;
            const q = this.search.toLowerCase();
            return this.computedOptions.filter((o) =>
                String(o.label).toLowerCase().includes(q),
            );
        },

        get selectedValues() {
            const val = this.value;
            if (this.multiple) {
                return Array.isArray(val) ? val.map(String) : [];
            }
            return val !== '' && val !== null && val !== undefined
                ? [String(val)]
                : [];
        },

        get hasValue() {
            return this.selectedValues.length > 0;
        },

        get displayText() {
            if (this.multiple) {
                const count = this.selectedValues.length;
                if (count === 0) return '';
                if (count === 1) {
                    const opt = this.computedOptions.find(
                        (o) =>
                            String(o.value) === String(this.selectedValues[0]),
                    );
                    return opt ? opt.label : '';
                }
                return count + ' выбрано';
            }
            const val = this.value;
            if (val === '' || val === null || val === undefined) return '';
            const opt = this.computedOptions.find(
                (o) => String(o.value) === String(val),
            );
            return opt ? opt.label : '';
        },

        _fireOnSelect(option) {
            if (!this._onSelectCallback) return;
            const fn = this.$data[this._onSelectCallback];
            if (typeof fn === 'function') {
                this.$nextTick(() => {
                    fn.call(this.$data, option.value, option);
                });
            }
        },

        // --- Methods ---
        toggle() {
            if (this.disabled) return;
            this.open ? this.close() : this.openDropdown();
        },

        openDropdown() {
            if (this.disabled) return;
            this.open = true;
            this.search = '';
            this.highlightIndex = -1;
            this.$nextTick(() => {
                this.$refs.search?.focus();
                this.scrollToSelected();
            });
        },

        close() {
            this.open = false;
            this.search = '';
            this.highlightIndex = -1;
        },

        select(option) {
            if (this.multiple) {
                this.toggleMultiple(option);
            } else {
                this.selectSingle(option);
            }

            // Сразу пишем в родителя ПОСЛЕ изменения value
            this._writeToParent(this.value);

            const data = this.$data;
            if (typeof data?.clearError === 'function') {
                data.clearError(this._errorKey);
            }

            this._fireOnSelect(option);
        },

        selectSingle(option) {
            this.value = option.value;
            this.close();
        },

        toggleMultiple(option) {
            let arr = Array.isArray(this.value) ? [...this.value] : [];
            const strVal = String(option.value);
            const idx = arr.findIndex((v) => String(v) === strVal);
            if (idx === -1) {
                arr.push(option.value);
            } else {
                arr.splice(idx, 1);
            }
            this.value = arr;
        },

        removeTag(val) {
            this.value = this.value.filter((v) => String(v) !== String(val));
            this._writeToParent(this.value);
        },

        isSelected(option) {
            return this.selectedValues.includes(String(option.value));
        },

        clear() {
            this.value = this.multiple ? [] : '';
            this._writeToParent(this.value);
            this.close();
        },

        getLabelByValue(val) {
            const opt = this.computedOptions.find(
                (o) => String(o.value) === String(val),
            );
            return opt ? opt.label : val;
        },

        onKeydown(e) {
            if (!this.open) {
                if (['Enter', 'Space', 'ArrowDown'].includes(e.code)) {
                    e.preventDefault();
                    this.openDropdown();
                }
                return;
            }

            switch (e.code) {
                case 'ArrowDown':
                    e.preventDefault();
                    this.highlightIndex = Math.min(
                        this.highlightIndex + 1,
                        this.filteredOptions.length - 1,
                    );
                    this.scrollToHighlighted();
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    this.highlightIndex = Math.max(this.highlightIndex - 1, 0);
                    this.scrollToHighlighted();
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (
                        this.highlightIndex >= 0 &&
                        this.filteredOptions[this.highlightIndex]
                    ) {
                        this.select(this.filteredOptions[this.highlightIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    this.close();
                    this.$refs.trigger?.focus();
                    break;
            }
        },

        scrollToHighlighted() {
            this.$nextTick(() => {
                const el = this.$refs.listbox?.children[this.highlightIndex];
                el?.scrollIntoView({ block: 'nearest' });
            });
        },

        scrollToSelected() {
            if (!this.hasValue) return;
            this.$nextTick(() => {
                const el = this.$refs.listbox?.querySelector(
                    '[data-selected="true"]',
                );
                el?.scrollIntoView({ block: 'nearest' });
            });
        },
    };
}
